import React from 'react';
import { cn } from '../lib/utils';

/**
 * Card — surface container with border + optional shadow variants.
 * Use <Card.Header>, <Card.Body>, <Card.Footer> for structured layouts.
 *
 * Default has NO shadow — clean flat design that matches the off-white
 * background. Use variant="elevated" or "brand" if you want elevation.
 */
export function Card({ className, variant = 'default', children, ...props }) {
  const variants = {
    default: 'bg-surface border border-border',
    elevated: 'bg-surface-2 border border-border-strong',
    ghost: 'bg-transparent border border-border',
    brand: 'bg-surface border border-brand/30',
  };
  return (
    <div
      className={cn(
        'rounded-lg overflow-hidden',
        variants[variant],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function Header({ className, children, ...props }) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 p-5 pb-3',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function Title({ className, children, ...props }) {
  return (
    <div>
      <h3 className={cn('text-base font-semibold text-fg', className)} {...props}>
        {children}
      </h3>
    </div>
  );
}

function Subtitle({ className, children, ...props }) {
  return (
    <p className={cn('text-xs text-fg-muted mt-1', className)} {...props}>
      {children}
    </p>
  );
}

function Body({ className, children, ...props }) {
  return (
    <div className={cn('p-5 pt-3', className)} {...props}>
      {children}
    </div>
  );
}

function Footer({ className, children, ...props }) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 p-4 border-t border-border bg-surface-2/30',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

Card.Header = Header;
Card.Title = Title;
Card.Subtitle = Subtitle;
Card.Body = Body;
Card.Footer = Footer;